import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { appServices } from '@/data/AppServices';
import type { Article, PlaybackSnapshot } from '@/domain/Models';
import { nextSequentialArticle, resumablePosition } from './playbackPolicy';

function fmt(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

function progressText(a: Article): string {
  if (a.durationMs <= 0) return '';
  const pct = Math.min(100, Math.round((a.lastPositionMs / a.durationMs) * 100));
  return `已听 ${pct}%`;
}

// =====================================================================
// 文章列表 ("文章 · 磨耳朵" + 导入菜单 + 随机练习 + 列表)
// =====================================================================
export function ArticleListPage() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showFormat, setShowFormat] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importMsg, setImportMsg] = useState<string | null>(null);
  const [todayDone, setTodayDone] = useState(0);
  const [todayTarget, setTodayTarget] = useState(1);
  const fileRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    try {
      const [list, done, settings] = await Promise.all([
        appServices.article.list(),
        appServices.article.todayCompletedCount(),
        appServices.settings.get(),
      ]);
      setArticles(list);
      setTodayDone(done);
      setTodayTarget(settings.dailyArticleTaskCount);
      setError(null);
    } catch (e) {
      setError(String(e));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function startRandom() {
    try {
      const step = await appServices.article.startRandomPlayback();
      if (step) navigate(`/article/${step.articleId}?mode=random`);
    } catch (e) {
      setError(String(e));
    }
  }

  async function importPack(file: File) {
    setImportMsg('正在导入文章包…');
    setMenuOpen(false);
    try {
      const r = await appServices.article.importPackage(file);
      setImportMsg(r.ok ? '文章包导入成功' : `导入失败：${r.errorCode}`);
      await refresh();
    } catch (e) {
      setImportMsg(`导入失败：${String(e)}`);
    }
  }

  async function removeArticle(a: Article) {
    if (!window.confirm(`删除这篇导入文章？`)) return;
    const r = await appServices.article.deleteImported(a.id);
    if (r.ok) await refresh();
    else setError(r.errorCode ?? '删除失败');
  }

  return (
    <div className="page stack">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <strong>文章 · 磨耳朵</strong>
        <button
          type="button"
          className="secondary"
          aria-label="导入文章"
          onClick={() => setMenuOpen((v) => !v)}
        >
          ＋
        </button>
      </div>
      {menuOpen && (
        <div className="card stack">
          <button
            type="button"
            onClick={() => {
              setMenuOpen(false);
              fileRef.current?.click();
            }}
          >
            导入 .bessarticle 文章包
          </button>
          <button
            type="button"
            className="secondary"
            onClick={() => {
              setMenuOpen(false);
              setShowFormat(true);
            }}
          >
            查看格式说明
          </button>
        </div>
      )}
      <input
        ref={fileRef}
        type="file"
        accept=".bessarticle,application/zip"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void importPack(f);
          e.target.value = '';
        }}
      />
      {showFormat && (
        <div className="card stack">
          <strong>.bessarticle 文章包格式</strong>
          <div className="muted">
            由储能英语实战各端导出的文章包（zip）：包含 manifest 与逐句字幕的音频。导入后可在「文章 · 磨耳朵」中收听。
          </div>
          <button type="button" onClick={() => setShowFormat(false)}>
            知道了
          </button>
        </div>
      )}
      {importMsg && (
        <div className="card">
          <div className="muted">{importMsg}</div>
          <button type="button" onClick={() => setImportMsg(null)}>
            知道了
          </button>
        </div>
      )}

      <div className="card stack">
        <div className="muted">
          今日文章任务 {Math.min(todayDone, todayTarget)}/{todayTarget}
          {todayDone >= todayTarget ? ' · 今日任务已完成' : ''}
        </div>
        <strong>随机练习</strong>
        <div className="muted">随机播放储能文章，整袋播放完前不会重复。</div>
        <button type="button" onClick={() => void startRandom()}>
          随机播放
        </button>
      </div>

      {error && <div className="error-box">{error}</div>}
      {articles.length === 0 ? (
        <div className="stack">
          <div className="muted">文章库为空。点击右上角 ＋，导入 .bessarticle 文章包。</div>
          <button
            type="button"
            onClick={() => {
              setMenuOpen(false);
              fileRef.current?.click();
            }}
          >
            导入 .bessarticle 文章包
          </button>
        </div>
      ) : (
        <div className="stack">
          {articles.map((a) => (
            <div className="list-item stack" key={a.id} style={{ gap: 6 }}>
              <button
                type="button"
                className="grow"
                style={{ textAlign: 'left', background: 'none', border: 'none', padding: 0 }}
                onClick={() => navigate(`/article/${a.id}`)}
              >
                <div style={{ fontWeight: 600 }}>{a.titleZh || a.title}</div>
                <div className="muted">
                  {a.titleZh ? a.title : ''} {fmt(a.durationMs)} · {a.topic}
                </div>
              </button>
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <div className="muted">{progressText(a)}</div>
                {a.source !== 'BUNDLED' && (
                  <div className="row" style={{ gap: 8 }}>
                    <span className="chip">导入文章</span>
                    <button
                      type="button"
                      className="secondary"
                      style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}
                      onClick={() => void removeArticle(a)}
                    >
                      删除
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =====================================================================
// 文章播放器 ("文章磨耳朵 / 随机磨耳朵", 底部固定播放条)
// =====================================================================
export function ArticlePlayerPage() {
  const { articleId = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isRandom = new URLSearchParams(location.search).get('mode') === 'random';

  const [article, setArticle] = useState<Article | null>(null);
  const [all, setAll] = useState<Article[]>([]);
  const [snap, setSnap] = useState<PlaybackSnapshot>(appServices.audio.snapshot());
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [singleLoop, setSingleLoop] = useState(false);
  const [randomRemaining, setRandomRemaining] = useState<number | null>(null);
  const [transitioning, setTransitioning] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const articleRef = useRef<Article | null>(null);
  const allRef = useRef<Article[]>([]);
  const snapRef = useRef<PlaybackSnapshot>(appServices.audio.snapshot());
  const singleLoopRef = useRef(false);
  const mountedRef = useRef(true);
  const transitioningRef = useRef(false);
  const playCommandRef = useRef(0);
  const lastHandledPlaybackRef = useRef(0);
  const lastSaveRef = useRef(0);
  const writeChainRef = useRef<Promise<void>>(Promise.resolve());

  const enqueueWrite = useCallback((work: () => Promise<void>): Promise<void> => {
    const next = writeChainRef.current.then(work, work);
    const handled = next.catch((e: unknown) => {
      if (mountedRef.current) setError(`保存播放进度失败：${String(e)}`);
    });
    writeChainRef.current = handled;
    return handled;
  }, []);

  const flushProgress = useCallback(
    (force: boolean): void => {
      const currentArticle = articleRef.current;
      const currentSnap = snapRef.current;
      if (!currentArticle || currentSnap.assetId !== currentArticle.audioAssetId) return;
      if (currentSnap.positionMs <= 0 || currentSnap.state === 'COMPLETED') return;
      const now = Date.now();
      if (!force && now - lastSaveRef.current < 800) return;
      lastSaveRef.current = now;
      const id = currentArticle.id;
      const positionMs = currentSnap.positionMs;
      void enqueueWrite(() => appServices.article.saveProgress(id, positionMs));
    },
    [enqueueWrite],
  );

  const playArticle = useCallback(async (target: Article, resumeProgress: boolean) => {
    const command = ++playCommandRef.current;
    setError(null);
    try {
      const speed = (await appServices.settings.get()).playbackSpeed;
      if (command !== playCommandRef.current) return;
      await appServices.audio.play(target.audioAssetId, speed);
      if (command !== playCommandRef.current) return;
      const currentSnap = appServices.audio.snapshot();
      if (currentSnap.assetId !== target.audioAssetId || currentSnap.state === 'FAILED') return;
      const resumeAt = resumeProgress ? resumablePosition(target) : 0;
      if (resumeAt > 0) await appServices.audio.seekTo(resumeAt);
    } catch (e) {
      if (command === playCommandRef.current && mountedRef.current) {
        setError(`音频播放失败：${String(e)}`);
      }
    }
  }, []);

  const activateArticle = useCallback(
    async (target: Article, remainingCount: number | null): Promise<void> => {
      articleRef.current = target;
      setArticle(target);
      setExpanded(new Set());
      setRandomRemaining(remainingCount);
      navigate(`/article/${target.id}${isRandom ? '?mode=random' : ''}`);
      await playArticle(target, false);
    },
    [isRandom, navigate, playArticle],
  );

  const advanceToNext = useCallback(
    async (currentArticle: Article): Promise<boolean> => {
      if (transitioningRef.current) return false;
      transitioningRef.current = true;
      setTransitioning(true);
      try {
        if (isRandom) {
          let step = await appServices.article.nextRandomPlayback(currentArticle.id);
          while (step) {
            const target =
              allRef.current.find((item) => item.id === step!.articleId) ??
              (await appServices.article.get(step.articleId));
            if (target) {
              await activateArticle(target, step.remainingCount);
              return true;
            }
            step = await appServices.article.nextRandomPlayback(step.articleId);
          }
          setRandomRemaining(0);
          return false;
        }

        const candidate = nextSequentialArticle(allRef.current, currentArticle.id);
        if (!candidate) return false;
        const target = (await appServices.article.get(candidate.id)) ?? candidate;
        await activateArticle(target, null);
        return true;
      } catch (e) {
        if (mountedRef.current) setError(`切换下一篇失败：${String(e)}`);
        return false;
      } finally {
        transitioningRef.current = false;
        if (mountedRef.current) setTransitioning(false);
      }
    },
    [activateArticle, isRandom],
  );

  useEffect(() => {
    let cancelled = false;
    const routeChangedOutsidePlayer =
      articleRef.current != null && articleRef.current.id !== articleId;
    if (routeChangedOutsidePlayer) {
      flushProgress(true);
      playCommandRef.current += 1;
      void appServices.audio.stop();
    }
    void (async () => {
      try {
        setError(null);
        if (isRandom) {
          const step = await appServices.article.startRandomPlayback();
          if (!step) {
            if (!cancelled) setError('文章库为空');
            return;
          }
          if (step.articleId !== articleId) {
            navigate(`/article/${step.articleId}?mode=random`, { replace: true });
            return;
          }
          if (!cancelled) setRandomRemaining(step.remainingCount);
        }

        const [loadedArticle, list] = await Promise.all([
          appServices.article.get(articleId),
          appServices.article.list(),
        ]);
        if (cancelled) return;
        if (!loadedArticle) {
          setError('文章不存在或已被删除');
          setArticle(null);
          articleRef.current = null;
          return;
        }
        articleRef.current = loadedArticle;
        allRef.current = list;
        setArticle(loadedArticle);
        setAll(list);
      } catch (e) {
        if (!cancelled) setError(String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [articleId, flushProgress, isRandom, navigate]);

  useEffect(() => {
    const unsub = appServices.audio.subscribe((next) => {
      snapRef.current = next;
      setSnap(next);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (snap.state === 'PAUSED') flushProgress(true);
    else if (snap.state === 'PLAYING') flushProgress(false);
  }, [flushProgress, snap.positionMs, snap.state]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flushProgress(true);
    };
    const onPageHide = () => flushProgress(true);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [flushProgress]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      flushProgress(true);
      mountedRef.current = false;
      playCommandRef.current += 1;
      void appServices.audio.stop();
    };
  }, [flushProgress]);

  useEffect(() => {
    const currentArticle = articleRef.current;
    if (
      snap.state !== 'FAILED' ||
      !currentArticle ||
      snap.assetId !== currentArticle.audioAssetId
    ) {
      return;
    }
    setError(`音频播放失败：${snap.errorCode ?? 'AUDIO_PLAYBACK_FAILED'}，请点击播放重试`);
  }, [snap.assetId, snap.errorCode, snap.state]);

  useEffect(() => {
    const completedArticle = articleRef.current;
    if (
      snap.state !== 'COMPLETED' ||
      !completedArticle ||
      snap.assetId !== completedArticle.audioAssetId ||
      snap.playbackId <= lastHandledPlaybackRef.current
    ) {
      return;
    }
    lastHandledPlaybackRef.current = snap.playbackId;
    void enqueueWrite(() =>
      appServices.article.completePlayback(completedArticle.id, completedArticle.durationMs),
    );
    void (async () => {
      if (singleLoopRef.current) await playArticle(completedArticle, false);
      else await advanceToNext(completedArticle);
    })();
  }, [advanceToNext, enqueueWrite, playArticle, snap.assetId, snap.playbackId, snap.state]);

  // 当前段落高亮 + 自动滚动
  const displayPosition = article && snap.assetId === article.audioAssetId ? snap.positionMs : 0;
  const activeIdx = article
    ? article.paragraphs.findIndex(
        (p) =>
          p.startMs != null &&
          p.endMs != null &&
          displayPosition >= p.startMs &&
          displayPosition < p.endMs,
      )
    : -1;
  useEffect(() => {
    if (activeIdx < 0 || !scrollRef.current) return;
    const el = scrollRef.current.children[activeIdx] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [activeIdx]);

  if (!article) {
    return (
      <div className="page">
        {error ? <div className="error-box">{error}</div> : <div className="muted">加载中…</div>}
      </div>
    );
  }

  const hasSubtitles = article.paragraphs.some((p) => p.textZh?.trim());
  const currentArticle = article;
  const playbackError =
    snap.state === 'FAILED' && snap.assetId === article.audioAssetId
      ? `音频播放失败：${snap.errorCode ?? 'AUDIO_PLAYBACK_FAILED'}，请点击播放重试`
      : null;
  const index = all.findIndex((a) => a.id === article.id);
  const canNext = isRandom
    ? (randomRemaining ?? 0) > 0
    : index >= 0 && index + 1 < all.length;

  async function toggleParagraph(i: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }

  async function next() {
    flushProgress(true);
    await advanceToNext(currentArticle);
  }

  return (
    <div className="app-shell no-nav" style={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="page stack" style={{ flex: 1, paddingBottom: 130 }}>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <button className="secondary" type="button" onClick={() => navigate('/article')}>
            ← 返回
          </button>
          <span className="chip">{isRandom ? '随机磨耳朵' : '文章磨耳朵'}</span>
        </div>
        <div className="muted">英文全文 · 点击有翻译的句子查看中文</div>

        {!hasSubtitles && (
          <div className="card muted">
            此文章未附字幕，可进行纯听练习。播放、暂停、拖动进度和下一篇功能仍可正常使用。
          </div>
        )}

        {(playbackError ?? error) && (
          <div className="error-box">{playbackError ?? error}</div>
        )}

        <div className="stack" ref={scrollRef}>
          {article.paragraphs.map((p, i) => (
            <div
              key={i}
              className={`card${i === activeIdx ? ' paragraph-active' : ''}`}
              style={{ cursor: p.textZh ? 'pointer' : 'default' }}
              onClick={() => {
                if (p.textZh) void toggleParagraph(i);
              }}
            >
              <div>{p.textEn}</div>
              {expanded.has(i) && p.textZh && <div className="muted">{p.textZh}</div>}
            </div>
          ))}
        </div>
      </div>

      {/* 底部固定播放条 */}
      <div className="article-player-bar">
        <div className="row" style={{ justifyContent: 'space-between', gap: 10 }}>
          <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {article.titleZh || article.title}
          </div>
          <button
            type="button"
            className="secondary article-loop-toggle"
            aria-pressed={singleLoop}
            onClick={() => {
              const nextValue = !singleLoopRef.current;
              singleLoopRef.current = nextValue;
              setSingleLoop(nextValue);
            }}
          >
            单曲循环
          </button>
        </div>
        <input
          type="range"
          min={0}
          max={Math.max(1, article.durationMs)}
          value={Math.min(displayPosition, article.durationMs)}
          onChange={(e) => void appServices.audio.seekTo(Number(e.target.value))}
          aria-label="播放进度"
        />
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <span className="muted">
            {fmt(displayPosition)} / {fmt(article.durationMs)}
          </span>
          <div className="row" style={{ gap: 10 }}>
            <button
              type="button"
              className="secondary"
              aria-label="播放或暂停"
              onClick={async () => {
                if (snap.state === 'PLAYING') await appServices.audio.pause();
                else if (snap.state === 'PAUSED' || snap.state === 'BUFFERING')
                  await appServices.audio.resume();
                else await playArticle(article, snap.state === 'IDLE');
              }}
            >
              {snap.state === 'PLAYING' ? '⏸' : '▶'}
            </button>
            <button
              type="button"
              className="secondary"
              aria-label="下一篇"
              disabled={!canNext || transitioning}
              onClick={() => void next()}
            >
              下一篇
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
